import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Edit2, Plus, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import {
  getRecipeComponents,
  createRecipeComponent,
  updateRecipeComponent,
  deleteRecipeComponent,
  getRecipeComponentItems,
  createRecipeComponentItem,
  deleteRecipeComponentItem,
  getModifiers,
  createModifier,
  updateModifier,
  deleteModifier,
} from '../api/modifiers';
import { formatApiError } from '../utils/apiErrors';

const emptyModifierOption = (sortOrder = 0) => ({
  id: null,
  name: '',
  raw_material: '',
  default_quantity: 1,
  price_adjustment: 0,
  sort_order: sortOrder,
  is_active: true,
  is_available: true,
});

export const revealModifierOption = (getElement, schedule = window.requestAnimationFrame) => {
  schedule(() => getElement()?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
};

const sortModifierOptions = (options = []) => [...options].sort((a, b) => {
  const aOrder = Number.isFinite(parseInt(a.sort_order, 10)) ? parseInt(a.sort_order, 10) : 0;
  const bOrder = Number.isFinite(parseInt(b.sort_order, 10)) ? parseInt(b.sort_order, 10) : 0;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
});

export const buildModifierOptionsPayload = (options = []) => {
  const incompleteIndex = options.findIndex(option => {
    const hasName = Boolean(String(option.name || '').trim());
    const hasInventoryItem = Boolean(option.raw_material);
    return hasName !== hasInventoryItem;
  });

  if (incompleteIndex >= 0) {
    throw new Error(`Option ${incompleteIndex + 1}: enter both the option name and inventory item.`);
  }

  return sortModifierOptions(options
    .filter(option => String(option.name || '').trim() && option.raw_material)
    .map(option => ({
      ...(option.id ? { id: option.id } : {}),
      name: String(option.name).trim(),
      raw_material: option.raw_material,
      default_quantity: option.default_quantity,
      price_adjustment: option.price_adjustment,
      sort_order: option.sort_order,
      is_active: option.is_active,
      is_available: option.is_available !== false,
    })));
};

const ModifierManager = ({
  defaultTab = 'components',
  hideTabs = false,
  useModalForms = false,
  openEditId = null,
  onOpenEditHandled,
  onDataChanged,
  showUnavailableModifierItems = true,
  summaryContent = null,
}) => {
  const [recipeComponents, setRecipeComponents] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);
  const modifierOptionRefs = useRef([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(true);

  const [componentForm, setComponentForm] = useState({ name: '', description: '', is_active: true });
  const [componentItems, setComponentItems] = useState([]);
  const [editingComponent, setEditingComponent] = useState(null);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [componentCloneSourceId, setComponentCloneSourceId] = useState('');

  const [groupForm, setGroupForm] = useState({
    name: '',
    display_name: '',
    auto_split_quantity: false,
    auto_split_basis_quantity: 0,
    sort_order: 0,
    is_active: true,
    allowed_categories: [],
  });
  const [editingGroup, setEditingGroup] = useState(null);
  const [modifierOptionsForm, setModifierOptionsForm] = useState([emptyModifierOption()]);
  const [expandedModifierOptions, setExpandedModifierOptions] = useState([0]);
  const [expandedSavedModifiers, setExpandedSavedModifiers] = useState({});
  const [showModifierModal, setShowModifierModal] = useState(false);
  const [modifierCloneSourceId, setModifierCloneSourceId] = useState('');

  const modalForms = useModalForms || hideTabs;

  const loadData = async () => {
    try {
      const [componentRes, groupRes, rawRes, categoryRes] = await Promise.all([
        getRecipeComponents(),
        getModifiers(),
        api.get('/raw-materials/?active=true&page_size=500'),
        api.get('/categories/?active=true&page_size=500'),
      ]);
      setRecipeComponents(componentRes.data.results || componentRes.data);
      setModifierGroups(groupRes.data.results || groupRes.data);
      setRawMaterials(rawRes.data.results || rawRes.data);
      setCategories(categoryRes.data.results || categoryRes.data);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load modifier data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetComponentForm = () => {
    setComponentForm({ name: '', description: '', is_active: true });
    setComponentItems([]);
    setEditingComponent(null);
    setComponentCloneSourceId('');
  };

  const resetGroupForm = () => {
    setGroupForm({
      name: '',
      display_name: '',
      auto_split_quantity: false,
      auto_split_basis_quantity: 0,
      sort_order: 0,
      is_active: true,
      allowed_categories: [],
    });
    setEditingGroup(null);
    setModifierOptionsForm([emptyModifierOption()]);
    setExpandedModifierOptions([0]);
    setModifierCloneSourceId('');
  };

  const openAddComponent = () => {
    setActiveTab('components');
    resetComponentForm();
    setShowComponentModal(true);
  };

  const openAddModifier = () => {
    setActiveTab('modifiers');
    resetGroupForm();
    setShowModifierModal(true);
  };

  const closeComponentModal = () => {
    setShowComponentModal(false);
    resetComponentForm();
  };

  const closeModifierModal = () => {
    setShowModifierModal(false);
    resetGroupForm();
  };

  const applyComponentTemplate = async (component) => {
    if (!component) return;
    const itemsRes = await getRecipeComponentItems(component.id);
    setEditingComponent(null);
    setComponentForm({
      name: component.name || '',
      description: component.description || '',
      is_active: component.is_active !== false,
    });
    setComponentItems((itemsRes.data.results || itemsRes.data || []).map((item) => ({
      raw_material: item.raw_material,
      quantity: item.quantity,
    })));
    setComponentCloneSourceId(String(component.id));
  };

  const applyModifierTemplate = (group) => {
    if (!group) return;
    setEditingGroup(null);
    setGroupForm({
      name: group.name || '',
      display_name: group.display_name || '',
      auto_split_quantity: group.auto_split_quantity || false,
      auto_split_basis_quantity: group.auto_split_basis_quantity || 0,
      sort_order: group.sort_order || 0,
      is_active: group.is_active !== false,
      allowed_categories: (group.allowed_categories_detail || []).map(category => category.id),
    });
    setModifierOptionsForm(
      (group.options || []).length > 0
        ? sortModifierOptions(group.options).map(option => ({
            id: null,
            name: option.name,
            raw_material: option.raw_material,
            default_quantity: option.default_quantity,
            price_adjustment: option.price_adjustment,
            sort_order: option.sort_order,
            is_active: option.is_active,
            is_available: option.is_available !== false,
          }))
        : [emptyModifierOption()]
    );
    setExpandedModifierOptions([]);
    setModifierCloneSourceId(String(group.id));
  };

  const handleEditComponent = async (component) => {
    try {
      const itemsRes = await getRecipeComponentItems(component.id);
      setEditingComponent(component);
      setComponentForm({
        name: component.name,
        description: component.description || '',
        is_active: component.is_active,
      });
      setComponentItems(itemsRes.data.results || itemsRes.data);
      setComponentCloneSourceId('');
      setActiveTab('components');
      if (modalForms) setShowComponentModal(true);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load component items'));
    }
  };

  const handleSaveComponent = async () => {
    try {
      let componentId = editingComponent?.id;
      if (editingComponent) {
        await updateRecipeComponent(componentId, componentForm);
      } else {
        const res = await createRecipeComponent(componentForm);
        componentId = res.data.id;
      }

      const existingItemsRes = componentId ? await getRecipeComponentItems(componentId) : { data: [] };
      const existingItems = existingItemsRes.data.results || existingItemsRes.data;
      for (const existing of existingItems) {
        await deleteRecipeComponentItem(existing.id);
      }
      for (const item of componentItems.filter(item => item.raw_material)) {
        await createRecipeComponentItem({
          recipe_component: componentId,
          raw_material: item.raw_material,
          quantity: item.quantity,
        });
      }

      toast.success('Recipe component saved');
      if (modalForms) setShowComponentModal(false);
      resetComponentForm();
      await loadData();
      onDataChanged?.();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save recipe component'));
    }
  };

  const handleDeleteComponent = async (component) => {
    if (!window.confirm(`Delete component "${component.name}"?`)) return;
    try {
      await deleteRecipeComponent(component.id);
      toast.success('Recipe component deleted');
      await loadData();
      onDataChanged?.();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete recipe component'));
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      display_name: group.display_name,
      auto_split_quantity: group.auto_split_quantity,
      auto_split_basis_quantity: group.auto_split_basis_quantity,
      sort_order: group.sort_order,
      is_active: group.is_active,
      allowed_categories: (group.allowed_categories_detail || []).map(category => category.id),
    });
    setModifierOptionsForm(
      (group.options || []).length > 0
        ? sortModifierOptions(group.options).map(option => ({
            id: option.id,
            name: option.name,
            raw_material: option.raw_material,
            default_quantity: option.default_quantity,
            price_adjustment: option.price_adjustment,
            sort_order: option.sort_order,
            is_active: option.is_active,
            is_available: option.is_available !== false,
          }))
        : [emptyModifierOption()]
    );
    setExpandedModifierOptions([]);
    setActiveTab('modifiers');
    if (modalForms) setShowModifierModal(true);
    setModifierCloneSourceId('');
  };

  const handleSaveGroup = async () => {
    let sortedOptions;
    try {
      sortedOptions = buildModifierOptionsPayload(modifierOptionsForm);
    } catch (error) {
      toast.error(error.message);
      return;
    }

    try {
      const payload = {
        ...groupForm,
        allowed_categories: groupForm.allowed_categories,
        options: sortedOptions,
      };

      if (editingGroup) {
        await updateModifier(editingGroup.id, payload);
      } else {
        await createModifier(payload);
      }

      toast.success('Modifier saved');
      if (modalForms) setShowModifierModal(false);
      resetGroupForm();
      await loadData();
      onDataChanged?.();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save modifier'));
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete modifier "${group.display_name || group.name}"?`)) return;
    try {
      await deleteModifier(group.id);
      toast.success('Modifier deleted');
      await loadData();
      onDataChanged?.();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete modifier'));
    }
  };

  useEffect(() => {
    if (!openEditId || loading) return;

    const openTarget = async () => {
      try {
        if (defaultTab === 'components') {
          let component = recipeComponents.find(comp => String(comp.id) === String(openEditId));
          if (!component) {
            const res = await api.get(`/recipe-components/${openEditId}/`);
            component = res.data;
          }
          await handleEditComponent(component);
        } else if (defaultTab === 'modifiers') {
          let group = modifierGroups.find(modifier => String(modifier.id) === String(openEditId));
          if (!group) {
            const res = await api.get(`/modifiers/${openEditId}/`);
            group = res.data;
          }
          handleEditGroup(group);
        }
      } catch (error) {
        toast.error(formatApiError(error, 'Could not open selected record'));
      } finally {
        onOpenEditHandled?.();
      }
    };

    openTarget();
  }, [openEditId, loading, defaultTab, recipeComponents, modifierGroups]);

  const addModifierOptionRow = () => {
    const nextIndex = modifierOptionsForm.length;
    setModifierOptionsForm([
      ...modifierOptionsForm,
      emptyModifierOption(nextIndex),
    ]);
    setExpandedModifierOptions(current => [...current, nextIndex]);
    revealModifierOption(() => modifierOptionRefs.current[nextIndex]);
  };

  const toggleModifierOptionRow = (index) => {
    setExpandedModifierOptions((current) => (
      current.includes(index)
        ? current.filter((rowIndex) => rowIndex !== index)
        : [...current, index]
    ));
  };

  const toggleSavedModifier = (modifierId) => {
    setExpandedSavedModifiers((current) => ({
      ...current,
      [modifierId]: !current[modifierId],
    }));
  };

  const updateModifierOptionRow = (index, field, value) => {
    const next = [...modifierOptionsForm];
    next[index] = {
      ...next[index],
      [field]: value,
    };
    setModifierOptionsForm(next);
  };

  const removeModifierOptionRow = (index) => {
    if (modifierOptionsForm.length === 1) {
      setModifierOptionsForm([emptyModifierOption()]);
      setExpandedModifierOptions([0]);
      return;
    }
    setModifierOptionsForm(modifierOptionsForm.filter((_, rowIndex) => rowIndex !== index));
    setExpandedModifierOptions((current) => current
      .filter((rowIndex) => rowIndex !== index)
      .map((rowIndex) => (rowIndex > index ? rowIndex - 1 : rowIndex)));
  };

  const updateAllowedCategory = (categoryId, checked) => {
    setGroupForm((current) => ({
      ...current,
      allowed_categories: checked
        ? [...current.allowed_categories, categoryId]
        : current.allowed_categories.filter(id => String(id) !== String(categoryId)),
    }));
  };

  const renderModal = (title, onClose, children) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl p-4 sm:p-6 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );

  const renderComponentForm = () => (
    <div className="space-y-4">
      <div className={`grid grid-cols-1 ${editingComponent ? '' : 'sm:grid-cols-2'} gap-4`}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Component Name</label>
          <input
            value={componentForm.name}
            onChange={(e) => setComponentForm({ ...componentForm, name: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        {!editingComponent && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Clone as ...</label>
            <select
              value={componentCloneSourceId}
              onChange={async (e) => {
                const componentId = e.target.value;
                if (!componentId) {
                  resetComponentForm();
                  return;
                }
                try {
                  const component = recipeComponents.find((entry) => String(entry.id) === String(componentId));
                  await applyComponentTemplate(component);
                  toast.success(`Cloned from ${component?.name || 'component'}`);
                } catch (error) {
                  toast.error(formatApiError(error, 'Failed to clone component'));
                }
              }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">Start blank</option>
              {recipeComponents.map((component) => (
                <option key={component.id} value={component.id}>{component.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={componentForm.description}
          onChange={(e) => setComponentForm({ ...componentForm, description: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg"
          rows={3}
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Component Items</label>
          <button
            type="button"
            onClick={() => setComponentItems([...componentItems, { raw_material: '', quantity: 1 }])}
            className="text-primary-600 text-sm flex items-center gap-1"
          >
            <Plus size={16} /> Add Item
          </button>
        </div>
        {componentItems.map((item, index) => (
          <div key={index} className="flex gap-2 items-center">
            <select
              value={item.raw_material}
              onChange={(e) => {
                const next = [...componentItems];
                next[index].raw_material = e.target.value;
                setComponentItems(next);
              }}
              className="flex-1 px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">Select Raw Material</option>
              {rawMaterials.map(material => (
                <option key={material.id} value={material.id}>{material.name} ({material.unit_abbr})</option>
              ))}
            </select>
            <input
              type="number"
              step="0.001"
              value={item.quantity}
              onChange={(e) => {
                const next = [...componentItems];
                next[index].quantity = e.target.value;
                setComponentItems(next);
              }}
              className="w-24 px-3 py-2 border rounded-lg text-sm"
            />
            <button
              type="button"
              onClick={() => setComponentItems(componentItems.filter((_, i) => i !== index))}
              className="p-2 text-red-500"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-2">
        <button type="button" onClick={modalForms ? closeComponentModal : resetComponentForm} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
          Cancel
        </button>
        <button type="button" onClick={handleSaveComponent} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          Save Component
        </button>
      </div>
    </div>
  );

  const renderComponentList = () => (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={openAddComponent} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <Plus size={18} /> Add Component
        </button>
      </div>
      {summaryContent}
      <div className="space-y-3">
        {recipeComponents.map(component => (
          <div key={component.id} className="bg-white border rounded-xl p-4 flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-gray-800">{component.name}</p>
              <p className="text-sm text-gray-500">Cost: Rs. {parseFloat(component.total_cost || 0).toFixed(2)}</p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => handleEditComponent(component)} className="p-2 border rounded-lg text-blue-600 hover:bg-blue-50">
                <Edit2 size={16} />
              </button>
              <button type="button" onClick={() => handleDeleteComponent(component)} className="p-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {recipeComponents.length === 0 && (
          <div className="text-center py-10 text-gray-500 bg-white rounded-xl border">No components added</div>
        )}
      </div>
    </div>
  );

  const renderModifierForm = () => (
    <div className="space-y-4">
      <div className="border rounded-xl p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Internal Name</label>
            <input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          {!editingGroup ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Clone as ...</label>
              <select
                value={modifierCloneSourceId}
                onChange={(e) => {
                  const groupId = e.target.value;
                  if (!groupId) {
                    resetGroupForm();
                    return;
                  }
                  const group = modifierGroups.find((entry) => String(entry.id) === String(groupId));
                  applyModifierTemplate(group);
                  toast.success(`Cloned from ${group?.display_name || group?.name || 'modifier'}`);
                }}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">Start blank</option>
                {modifierGroups.map((group) => (
                  <option key={group.id} value={group.id}>{group.display_name || group.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input value={groupForm.display_name} onChange={(e) => setGroupForm({ ...groupForm, display_name: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          )}
        </div>
        {!editingGroup && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input value={groupForm.display_name} onChange={(e) => setGroupForm({ ...groupForm, display_name: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
            <input
              type="number"
              value={groupForm.sort_order}
              onChange={(e) => setGroupForm({ ...groupForm, sort_order: parseInt(e.target.value || 0, 10) })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="0"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={groupForm.auto_split_quantity}
            onChange={(e) => setGroupForm({ ...groupForm, auto_split_quantity: e.target.checked })}
          />
          Auto split total quantity across selected items
        </label>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Auto Split Basis Quantity</label>
          <input
            type="number"
            step="0.001"
            value={groupForm.auto_split_basis_quantity}
            onChange={(e) => setGroupForm({ ...groupForm, auto_split_basis_quantity: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Example: 300 grams total fruit"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Allowed Categories</label>
          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-auto border rounded-lg p-3">
            {categories.map(category => (
              <label key={category.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={groupForm.allowed_categories.map(String).includes(String(category.id))}
                  onChange={(e) => updateAllowedCategory(category.id, e.target.checked)}
                />
                {category.name}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="border rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-gray-800">Modifier Options</h3>
          <button type="button" onClick={addModifierOptionRow} className="text-primary-600 text-sm flex items-center gap-1">
            <Plus size={16} /> Add Option
          </button>
        </div>
        <div className="space-y-3">
          {modifierOptionsForm.map((option, index) => (
            <div
              key={option.id || index}
              ref={(element) => { modifierOptionRefs.current[index] = element; }}
              className="border rounded-lg"
            >
              <div className="flex items-center justify-between gap-3 p-3">
                <button type="button" onClick={() => toggleModifierOptionRow(index)} className="flex-1 flex items-center justify-between gap-3 text-left">
                  <div>
                    <p className="font-medium text-gray-800">{option.name || `Option ${index + 1}`}</p>
                    {(() => {
                      const mat = rawMaterials.find(m => String(m.id) === String(option.raw_material));
                      const invCost = mat ? parseFloat(mat.purchase_price || 0) * parseFloat(option.default_quantity || 0) : 0;
                      return (
                        <p className="text-sm text-gray-500">
                          {mat?.name || 'No inventory item selected'} | Qty {parseFloat(option.default_quantity || 0).toFixed(3)}
                          {' | '}
                          <span className="text-orange-600 font-semibold bg-orange-50 px-1.5 py-0.5 rounded text-xs">Inv Cost: Rs. {invCost.toFixed(2)}</span>
                          {parseFloat(option.price_adjustment || 0) !== 0 && (
                            <span className="text-green-700 font-semibold bg-green-50 px-1.5 py-0.5 rounded text-xs ml-1">+Rs. {parseFloat(option.price_adjustment).toFixed(2)}</span>
                          )}
                        </p>
                      );
                    })()}
                  </div>
                  {expandedModifierOptions.includes(index) ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
                </button>
                <button type="button" onClick={() => removeModifierOptionRow(index)} className="p-2 text-red-500">
                  <Trash2 size={16} />
                </button>
              </div>
              {expandedModifierOptions.includes(index) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 border-t p-3">
                  <input value={option.name} onChange={(e) => updateModifierOptionRow(index, 'name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Option name" />
                  <select value={option.raw_material} onChange={(e) => updateModifierOptionRow(index, 'raw_material', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Inventory Item</option>
                    {rawMaterials.map(material => (
                      <option key={material.id} value={material.id}>{material.name} ({material.unit_abbr})</option>
                    ))}
                  </select>
                  <input type="number" step="0.001" value={option.default_quantity} onChange={(e) => updateModifierOptionRow(index, 'default_quantity', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Default qty" />
                  <input type="number" step="0.01" value={option.price_adjustment} onChange={(e) => updateModifierOptionRow(index, 'price_adjustment', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Price adj." />
                  <input type="number" value={option.sort_order} onChange={(e) => updateModifierOptionRow(index, 'sort_order', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Sort" />
                  <label className={`col-span-full flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer select-none border ${option.is_available ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                    <input type="checkbox" checked={!!option.is_available} onChange={(e) => updateModifierOptionRow(index, 'is_available', e.target.checked)} className="w-4 h-4" />
                    <span className={`text-sm font-medium ${option.is_available ? 'text-green-700' : 'text-red-700'}`}>
                      {option.is_available ? 'Available' : 'Not Available'}
                    </span>
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button type="button" onClick={modalForms ? closeModifierModal : resetGroupForm} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
          Cancel
        </button>
        <button type="button" onClick={handleSaveGroup} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          Save Modifier
        </button>
      </div>
    </div>
  );

  const renderModifierList = () => (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={openAddModifier} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <Plus size={18} /> Add Modifier
        </button>
      </div>
      {summaryContent}
      <div className="space-y-3">
        {modifierGroups
          .filter((group) => (
            showUnavailableModifierItems
              || (group.options || []).some((option) => option.is_available !== false)
          ))
          .map(group => {
            const visibleOptions = showUnavailableModifierItems
              ? sortModifierOptions(group.options || [])
              : sortModifierOptions((group.options || []).filter((option) => option.is_available !== false));
            return (
          <div key={group.id} className="bg-white border rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <button type="button" onClick={() => toggleSavedModifier(group.id)} className="flex-1 flex items-center justify-between gap-3 text-left">
                <div>
                  <p className="font-medium text-gray-800">{group.display_name || group.name}</p>
                  <p className="text-sm text-gray-500">{visibleOptions.length || 0} options</p>
                </div>
                {expandedSavedModifiers[group.id] ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={() => handleEditGroup(group)} className="p-2 border rounded-lg text-blue-600 hover:bg-blue-50">
                  <Edit2 size={16} />
                </button>
                <button type="button" onClick={() => handleDeleteGroup(group)} className="p-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {expandedSavedModifiers[group.id] && (
              <div className="space-y-2">
                {visibleOptions.length === 0 ? (
                  <p className="text-sm text-gray-400">No inventory options added yet.</p>
                ) : (visibleOptions.map(option => (
                  <div key={option.id} className="border rounded-lg p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-800">{option.name}</p>
                      {(() => {
                        const invCost = parseFloat(option.raw_material_unit_cost || 0) * parseFloat(option.default_quantity || 0);
                        return (
                          <p className="text-sm text-gray-500">
                            {option.raw_material_name} | {parseFloat(option.default_quantity || 0).toFixed(3)} {option.unit_name || ''}
                            {' | '}
                            <span className="text-orange-600 font-semibold bg-orange-50 px-1.5 py-0.5 rounded text-xs">Inv Cost: Rs. {invCost.toFixed(2)}</span>
                            {parseFloat(option.price_adjustment || 0) !== 0 && (
                              <span className="text-green-700 font-semibold bg-green-50 px-1.5 py-0.5 rounded text-xs ml-1">+Rs. {parseFloat(option.price_adjustment).toFixed(2)}</span>
                            )}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                )))}
              </div>
            )}
          </div>
        )})}
        {modifierGroups.length === 0 && (
          <div className="text-center py-10 text-gray-500 bg-white rounded-xl border">No modifiers added</div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="bg-white rounded-xl shadow-sm p-6">Loading modifier tools...</div>;
  }

  return (
    <div className="space-y-4">
      {!hideTabs && (
        <div className="flex gap-2 flex-wrap bg-white rounded-xl shadow-sm p-1">
          {[
            { key: 'components', label: 'Recipe Components' },
            { key: 'modifiers', label: 'Modifier' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === tab.key ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'components' && (
        modalForms ? renderComponentList() : (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
            {renderComponentForm()}
            {renderComponentList()}
          </div>
        )
      )}

      {activeTab === 'modifiers' && (
        modalForms ? renderModifierList() : (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
            {renderModifierForm()}
            {renderModifierList()}
          </div>
        )
      )}

      {showComponentModal && renderModal(
        editingComponent ? 'Edit Component' : 'Add Component',
        closeComponentModal,
        renderComponentForm()
      )}

      {showModifierModal && renderModal(
        editingGroup ? 'Edit Modifier' : 'Add Modifier',
        closeModifierModal,
        renderModifierForm()
      )}
    </div>
  );
};

export default ModifierManager;
